import { useCallback, useEffect, useMemo, useState } from 'react'

import { getMyMessagesUnreadCount } from '../_data/resident'
import { useResidentTenancy } from './use-resident-tenancy'
import {
  ResidentMessagesUnreadContext,
  ResidentMessagesUnreadContextValue,
} from './use-resident-messages-unread'

const POLL_INTERVAL_MS = 45000

interface ResidentMessagesUnreadProviderProps {
  children: React.ReactNode
}

/**
 * Tracks the resident's unread direct-message count for their selected tenancy,
 * so the "Home" nav entry can show a badge. Polls on an interval to stay fresh
 * and exposes a manual `refresh` the messages card calls right after marking the
 * thread read (so the badge clears immediately).
 *
 * @param {ResidentMessagesUnreadProviderProps} props
 * @returns {JSX.Element}
 */
const ResidentMessagesUnreadProvider = ({
  children,
}: ResidentMessagesUnreadProviderProps): JSX.Element => {
  const { selectedTenancy } = useResidentTenancy()
  const [unreadCount, setUnreadCount] = useState<number>(0)

  const tenancyId = selectedTenancy === null ? '' : selectedTenancy.id

  const refresh = useCallback(async (): Promise<void> => {
    if (tenancyId === '') {
      setUnreadCount(0)
      return
    }
    setUnreadCount(await getMyMessagesUnreadCount(tenancyId))
  }, [tenancyId])

  useEffect(() => {
    if (tenancyId === '') {
      setUnreadCount(0)
      return undefined
    }
    void refresh()
    const handle = setInterval(() => {
      void refresh()
    }, POLL_INTERVAL_MS)
    return () => {
      clearInterval(handle)
    }
  }, [tenancyId, refresh])

  const value = useMemo<ResidentMessagesUnreadContextValue>(
    () => ({ unreadCount, refresh }),
    [unreadCount, refresh],
  )

  return (
    <ResidentMessagesUnreadContext.Provider value={value}>
      {children}
    </ResidentMessagesUnreadContext.Provider>
  )
}

export { ResidentMessagesUnreadProvider }
